import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from 'react-router-dom';
import React, { useEffect, useState } from 'react';

import Admin from './Admin/Admin';
import Contact from './Contact';
import Orders from './Orders';
import Resources from './Resources';
import ResourcesManagement from './ResourcesManagement';
import Sidebar from './Sidebar';
import { Image, Spinner } from '@nextui-org/react';
import { useAuth0 } from '@auth0/auth0-react';
import AirlockLogo from '../assets/airlock_partner_logo.png';

const Dashboard: React.FC = () => {
  const { isAuthenticated, isLoading, loginWithRedirect, user } = useAuth0();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      loginWithRedirect();
    } else if (!isLoading && isAuthenticated && user) {
      const roles = user['https://api.airlock/roles'] || [];
      setIsAdmin(roles.includes('admin'));
      setIsCheckingSession(false);
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, user]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  if (isLoading || isCheckingSession) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
        <div className="flex flex-col items-center gap-4 p-8 rounded-xl bg-white shadow-sm border border-slate-100">
          <Spinner size="lg" color="secondary" />
          <p className="text-sm font-medium text-slate-500">
            Loading Partner Portal...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Router>
      <div className="flex h-screen w-screen max-w-screen bg-slate-50">
        {/* Desktop sidebar */}
        <aside className="w-56 bg-white border-r border-slate-200 p-4 hidden md:flex flex-col flex-shrink-0">
          <Sidebar />
        </aside>

        {/* Mobile drawer overlay */}
        <div
          className={`fixed inset-0 z-50 md:hidden transition-opacity duration-300 ${
            isMobileMenuOpen
              ? 'opacity-100 pointer-events-auto'
              : 'opacity-0 pointer-events-none'
          }`}
        >
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          <div
            className={`absolute left-0 top-0 bottom-0 w-72 bg-white shadow-2xl flex flex-col transition-transform duration-300 ease-out ${
              isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {/* Drawer header */}
            <div className="flex items-center justify-between px-4 py-4 border-b border-slate-100">
              <Image
                src={AirlockLogo}
                alt="AIRLOCK"
                radius="none"
                width={160}
                className="object-contain"
              />
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            {/* Drawer body */}
            <div className="flex-1 overflow-y-auto p-4">
              <Sidebar onClose={() => setIsMobileMenuOpen(false)} />
            </div>
          </div>
        </div>

        {/* Main content area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile header */}
          <div className="md:hidden flex items-center justify-center px-4 py-3 bg-white border-b border-slate-200 relative">
            <button
              onClick={() => setIsMobileMenuOpen(true)}
              className="absolute left-4 p-1 text-slate-700"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </button>
            <Image
              src={AirlockLogo}
              alt="AIRLOCK"
              radius="none"
              width={180}
              className="object-contain"
            />
          </div>

          <main className="flex-1 overflow-y-auto">
            <div className="max-w-7xl mx-auto px-6 py-8">
              <Routes>
                <Route path="/orders" element={<Orders />} />
                <Route path="/resources" element={<Resources />} />
                <Route
                  path="/resources/manage"
                  element={
                    isAdmin ? <ResourcesManagement /> : <Navigate to="/resources" replace />
                  }
                />
                <Route path="/contact" element={<Contact />} />
                <Route path="/" element={<Navigate to="/orders" replace />} />
                <Route
                  path="/admin"
                  element={
                    isAdmin ? <Admin /> : <Navigate to="/orders" replace />
                  }
                />
              </Routes>
            </div>
          </main>
        </div>
      </div>
    </Router>
  );
};

export default Dashboard;
